"use client";

import { asLink, asText, isFilled } from "@prismicio/client";
import { PrismicNextLink } from "@prismicio/next";
import { PrismicRichText } from "@prismicio/react";
import { useId, useState } from "react";

const answerComponents = {
  paragraph: ({ children }) => (
    <p className="p-base-n nm text-bleu-90 not-last:mb-5">{children}</p>
  ),
  strong: ({ children }) => <strong className="p-base-n sbd">{children}</strong>,
  em: ({ children }) => <em>{children}</em>,
  list: ({ children }) => <ul className="my-5 pl-3">{children}</ul>,
  listItem: ({ children }) => (
    <li className="my-4 flex items-start gap-3 p-base-n nm text-bleu-90">
      <img
        src="/icons/bullet-point.svg"
        alt=""
        width={4}
        height={4}
        className="mt-2.5 size-1 shrink-0"
        aria-hidden
      />
      <span>{children}</span>
    </li>
  ),
  hyperlink: ({ children, node }) =>
    asLink(node.data) ? (
      <PrismicNextLink
        field={node.data}
        prefetch={false}
        className="p-base-n sbd text-vert underline"
      >
        {children}
      </PrismicNextLink>
    ) : (
      <span>{children}</span>
    ),
};

/**
 * @typedef {import("@prismicio/client").Content.BlogSliceFaqPrimary} FaqPrimary
 * @param {{ primary: FaqPrimary }} props
 */
const Faq = ({ primary }) => {
  const baseId = useId();
  const [openIndex, setOpenIndex] = useState(0);
  const questions = Array.isArray(primary.questions)
    ? primary.questions.filter(
        (item) => isFilled.richText(item.question) || isFilled.richText(item.answer),
      )
    : [];

  return (
    <div className="flex flex-col gap-7">
      {isFilled.richText(primary.title) && (
        <PrismicRichText
          field={primary.title}
          components={{
            heading2: ({ children }) => (
              <h2 className="h2-n lg:dk text-bleu">{children}</h2>
            ),
            heading3: ({ children }) => (
              <h3 className="h3-n lg:dk text-bleu">{children}</h3>
            ),
            heading4: ({ children }) => (
              <h4 className="h4-n lg:dk text-bleu">{children}</h4>
            ),
          }}
        />
      )}

      {questions.length > 0 && (
        <ul className="flex flex-col gap-5">
          {questions.map((item, index) => {
            const isOpen = openIndex === index;
            const questionId = `${baseId}-question-${index}`;
            const answerId = `${baseId}-answer-${index}`;
            const hasAnswer = isFilled.richText(item.answer);

            return (
              <li
                key={questionId}
                className={`overflow-hidden rounded-[20px] px-5 py-[30px] transition-colors duration-300 lg:px-10 ${isOpen ? "bg-bleu-5" : "bg-blanc"}`}
              >
                <h3>
                  <button
                    type="button"
                    id={questionId}
                    aria-expanded={isOpen}
                    aria-controls={hasAnswer ? answerId : undefined}
                    onClick={() => setOpenIndex(isOpen ? null : index)}
                    className="p-lg-n sbd w-full cursor-pointer text-left text-bleu focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-bleu"
                  >
                    {asText(item.question)}
                  </button>
                </h3>

                {hasAnswer && (
                  <div
                    id={answerId}
                    role="region"
                    aria-labelledby={questionId}
                    inert={!isOpen}
                    className={`grid transition-[grid-template-rows] duration-300 ease-out ${isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}
                  >
                    <div className="overflow-hidden">
                      <div className="pt-7">
                        <PrismicRichText
                          field={item.answer}
                          components={answerComponents}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default Faq;